import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const workSteps = [
  {
    icon: 'work-icon-01.svg',
    title: 'Step 1 - Use Case Definition and Success Metrics',
    desc: 'We translate goals into a decision or workflow AI will improve, define measurable KPIs, error costs, and a safe fallback when confidence is low.',
  },
  {
    icon: 'work-icon-02.svg',
    title: 'Step 2 - Data Readiness and Governance Setup',
    desc: 'We assess data sources, quality, labels, and permissions, then establish stable definitions, lineage, and privacy controls required for production.',
  },
  {
    icon: 'work-icon-03.svg',
    title: 'Step 3 - Model Design, Evaluation, and Prototyping',
    desc: 'We select an approach that fits your constraints (rules, classical ML, NLP, deep learning), build baselines, and validate performance with a representative test set.',
  },
  {
    icon: 'work-icon-04.svg',
    title: 'Step 4 - Production Integration and Deployment',
    desc: 'We deploy the AI capability as a secure service, integrate it into your product or operations, and implement monitoring, alerts, versioning, and rollback controls.',
  },
  {
    icon: 'work-icon-05.svg',
    title: 'Step 5 - Monitoring, Drift Management, and Improvement',
    desc: 'We track quality, drift, latency, and cost, establish feedback loops and review queues, and continuously improve accuracy using real-world outcomes.',
  },
];

const faqItems = [
  {
    question: 'What is the difference between AI integrations and GenAI solutions?',
    answer:
      'AI systems and integrations typically focus on classification, extraction, prediction, routing, and decision support using ML and NLP. Generative AI focuses on producing text or content. Many products use both, but they require different evaluation, safety controls, and monitoring strategies.',
  },
  {
    question: 'Which AI use cases usually deliver value fastest?',
    answer:
      'The fastest value usually comes from narrow, workflow-embedded use cases such as ticket and lead routing, document extraction, anomaly detection, forecasting, and prioritization. These are easier to measure, safer to operate, and quicker to integrate into daily operations than large, open-ended AI initiatives.',
  },
  {
    question: 'Do you build custom models or use third-party AI services?',
    answer:
      'Both. We choose based on data availability, latency requirements, privacy constraints, and expected differentiation. Third-party services can be effective for commodity capabilities. Custom models are often better when your domain vocabulary, labels, or decision rules are unique and accuracy is a competitive advantage.',
  },
  {
    question: 'How do you handle confidence and prevent wrong outputs from causing damage?',
    answer:
      'We design for safe behavior. That includes confidence thresholds, human review paths, exception queues, and deterministic validation before actions are taken. For high-impact use cases, we implement approval gates, audit logs, and clear fallback behavior when the model is uncertain.',
  },
  {
    question: 'How do you measure and monitor AI quality in production?',
    answer:
      'We use a combination of offline evaluation (fixed test sets and regression checks) and online monitoring (drift detection, outcome tracking, and feedback capture). We monitor quality signals, latency, error rates, and cost. We also add alerting so teams learn about issues before customers do.',
  },
  {
    question: 'Can you integrate AI into our existing SaaS, CRM, ERP, or support tools?',
    answer:
      'Yes. We integrate AI outputs into real workflows using APIs, webhooks, background jobs, and event-driven patterns. Common integrations include CRMs, ERPs, ticketing systems, identity providers, analytics platforms, and data warehouses, with proper access control and auditability.',
  },
  {
    question: 'What data do we need to start, and what if labels are missing?',
    answer:
      'We start with what you have and design a practical path to improvement. If labels are missing, we can create them through rule-assisted labeling, human review, or phased instrumentation. We also define how labels are governed so future training data stays consistent and reproducible.',
  },
];

const ServiceAISystems = () => {
  useEffect(() => {
    if (window.WOW) new window.WOW().init();
    if (window.initMagnetic) window.initMagnetic();
  }, []);

  return (
    <>
      <section className="breadcrumbs">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-wrapper">
                <div className="breadcrumb-cnt">
                  <span>Services Details</span>
                  <h1>AI Systems and Integrations</h1>
                  <div className="breadcrumb-list">
                    <Link to="/">Home</Link>
                    <img src="/assets/img/content/breadcrumb-arrow.svg" alt="" />
                    Services Details
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="service-details">
        <div className="about-services sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="about-content">
                  <div className="section-title-5">
                    <h2>
                      AI Systems <br /> and Integrations
                    </h2>
                  </div>

                  <p>
                    We build applied AI systems that improve decisions inside real workflows. This includes classification, extraction, forecasting, anomaly detection, and prioritization. Our focus is production reliability: clear success metrics, safe failure behavior, measurable performance, and monitoring that keeps quality stable as data and usage change.
                  </p>

                  <p>
                    AI projects fail when they stop at a prototype. We take them end to end: data readiness, model evaluation, deployment, integration, and operations. The result is an AI capability that teams can trust, support, and improve over time.
                  </p>

                  <ul className="about-featue">
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      ML and NLP for routing, triage, and decision support
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Document extraction and data structuring from PDFs and forms
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Predictive analytics for churn, risk, and demand forecasting
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Production deployment, monitoring, and drift management
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Human review workflows, confidence thresholds, and audit trails
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Integration into SaaS products and enterprise toolchains via APIs
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className="col-lg-6 wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="service-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/laptop.png" alt="Service" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="how-we-work-section sec-mar">
          <div className="container">
            <div
              className="row mb-60 wow animate fadeInUp"
              data-wow-delay="200ms"
              data-wow-duration="1500ms"
            >
              <div className="col-lg-12">
                <div className="section-title">
                  <h2>How We Work</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="work-item">
            <div className="container-fluid">
              <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 gy-5 justify-content-center">
                {workSteps.map((step, idx) => (
                  <div
                    key={step.title}
                    className="col magnetic-item wow animate fadeInUp"
                    data-wow-delay={`${200 + idx * 100}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="single-work">
                      <div className="work-icon">
                        <img src={`/assets/img/content/${step.icon}`} alt="" />
                      </div>
                      <div className="work-content">
                        <h3>{step.title}</h3>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="service-sort-driscription-area sec-mar">
          <div className="container">
            <div className="row">
              <div
                className="col-lg-12 wow animate fadeInUp"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="driscription-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/rectangular_image.png" alt="Description" />
                </div>
              </div>

              <div className="driscription-content">
                <div className="row">
                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="200ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      Effective AI requires a disciplined evaluation approach. We start with baseline methods and compare them against more advanced approaches only when the data supports it. We design a test set that reflects your real distribution, including difficult edge cases, then define acceptance criteria that match business risk, not only model accuracy.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Baselines first: rules, heuristics, classical ML, then deeper models
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Metrics aligned to outcomes: precision, recall, latency, and error cost
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Data governance: definitions, lineage, permissions, and retention
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Reproducible training and evaluation with versioned datasets
                      </li>
                    </ul>

                    <p>
                      Deployment is treated like a production service. We implement secure inference endpoints, structured logging, and performance monitoring. We also add safeguards such as confidence thresholds and review queues, so the system can be trusted even when data shifts over time.
                    </p>
                  </div>

                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="300ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      AI adoption improves when outputs are delivered at the right point in the workflow. We embed predictions and classifications into the tools teams use, such as CRMs, support platforms, portals, and internal dashboards. We also design feedback capture so human corrections become labeled data that improves future performance.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Support and success: ticket classification, SLA risk, next best action
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Sales ops: lead scoring, enrichment, routing, forecasting support
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Finance: invoice extraction, anomaly detection, reconciliation flags
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Healthcare and insurance: intake classification and audit-ready workflows
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Manufacturing and logistics: demand signals, incident prediction, QA triage
                      </li>
                    </ul>

                    <p>
                      Typical deliverables include a use case spec with KPIs, a data readiness and governance plan, evaluation results, an integrated inference service, and operational dashboards that track quality, drift, and cost over time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="service-faq-area sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/faqs.png" alt="FAQ" />
                </div>
              </div>

              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-content-area">
                  <h2>Frequently Asked Questions</h2>

                  <div className="accordion" id="accordionExample">
                    {faqItems.map((item, index) => (
                      <div key={item.question} className="accordion-item">
                        <h6 className="accordion-header" id={`heading${index}`}>
                          <button
                            className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${index}`}
                            aria-expanded={index === 0 ? 'true' : 'false'}
                            aria-controls={`collapse${index}`}
                          >
                            {item.question}
                          </button>
                        </h6>

                        <div
                          id={`collapse${index}`}
                          className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                          aria-labelledby={`heading${index}`}
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">{item.answer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceAISystems;



